import { useMemo } from 'react';
import { useParams } from 'react-router';
import { EditOutlined, WarningFilled } from '@ant-design/icons';
import { Button, Divider, Flex, Typography, Row, Col, Tooltip } from 'antd';
import { useQuery } from '@apollo/client/react';

import EditIncomeResidenceModal from './EditIncomeResidenceModal';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import { CUSTOMER_FINANCIAL_INFO } from 'queries/customer';

import useModalControls from 'hooks/useModalControls';
import { formatDate } from 'utilities/datesAndTimes';
import { parseCurrency, currencyFormatter } from 'utilities/helpers';
import useAccountQuery from 'hooks/useAccountQuery';
import useAgent from 'hooks/useAgent';

const { Title } = Typography;

const LOADING_SKELETON = {
  title: false,
  paragraph: { rows: 4, width: ['120px', '140px', '120px', '140px'] },
};

const IncomeResidence = () => {
  const agent = useAgent();
  const { customerId } = useParams();
  const { customerInDebtSale } = useAccountQuery(customerId);
  const editIncomeAndResidenceModal = useModalControls();

  const financialInfoQuery = useQuery(CUSTOMER_FINANCIAL_INFO, {
    variables: { customerId },
    notifyOnNetworkStatusChange: true,
  });

  const [annualIncome, monthlyLiving, shouldGetCLIPTout, timestamp] =
    useMemo(() => {
      let annualIncome = '';
      let monthlyLiving = '';
      let shouldGetCLIPTout = null;
      let timeStamp = '';

      const financialInfo = financialInfoQuery.data?.customer?.financialInfo;
      if (financialInfo) {
        annualIncome = parseCurrency(financialInfo?.annualIncome?.toString());
        monthlyLiving = parseCurrency(financialInfo?.monthlyLiving?.toString());
        shouldGetCLIPTout = financialInfo?.shouldGetCLIPTout;
        timeStamp = formatDate(financialInfo?.lastUpdated);
      }
      return [annualIncome, monthlyLiving, shouldGetCLIPTout, timeStamp];
    }, [financialInfoQuery.data]);

  return (
    <div>
      <EditIncomeResidenceModal
        {...editIncomeAndResidenceModal}
        customerId={customerId}
        monthlyLiving={monthlyLiving}
        annualIncome={annualIncome}
        EditIncomeResidenceModal
      />
      <Flex justify="space-between">
        <Title level={5}>
          Income & Residence{' '}
          {shouldGetCLIPTout && (
            <Tooltip title="Please confirm or update the customer's current information.">
              {' '}
              <WarningFilled
                data-testid="clip-tout-warning"
                style={{ color: 'var(color-warning)' }}
              />{' '}
            </Tooltip>
          )}
        </Title>
        <Tooltip
          title={
            customerInDebtSale && !agent.isSuperUser
              ? 'Requires Super User privilege to update customer details for customers with accounts in a debt sale'
              : ''
          }
        >
          <Button
            data-testid="edit-button"
            type="link"
            icon={<EditOutlined style={{ fontSize: '1.5rem' }} />}
            onClick={editIncomeAndResidenceModal.show}
            disabled={customerInDebtSale && !agent.isSuperUser}
            size="small"
            Button
          />
        </Tooltip>
      </Flex>
      <Divider style={{ margin: '0 0 var(--spacing-sm)' }} />
      <QueryBoundary query={financialInfoQuery} skeleton={LOADING_SKELETON}>
        <Row gutter={['var(--spacing)', 'var(--spacing-lg)']}>
          <Col span={24}>
            <DisplayValue label="Last Updated:">
              <span data-testid="last-updated">{timestamp}</span>
            </DisplayValue>
          </Col>
          <Col span={8}>
            <DisplayValue label="Monthly Rent/Mortgage:">
              {currencyFormatter(monthlyLiving) || '-'}
            </DisplayValue>
          </Col>
          <Col span={8}>
            <DisplayValue label="Total Annual Income:">
              {currencyFormatter(annualIncome) || '-'}
            </DisplayValue>
          </Col>
        </Row>
      </QueryBoundary>
    </div>
  );
};

export default IncomeResidence;
